import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const AboutPage = () => {
  // State for about page data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [aboutData, setAboutData] = useState({
    header: 'About Our Company',
    paragraph: 'We\'re dedicated to helping you find your perfect property',
    image_path: null,
    img: null // No default image, we'll handle this with CSS
  });
  
  const [valuesData, setValuesData] = useState({
    header: 'Our Values',
    paragraph: 'The principles that guide our business and service',
    values: [
      { title: 'Excellence', icon: 'faAward', description: 'We strive for excellence in everything we do.' },
      { title: 'Client-Focused', icon: 'faUsers', description: 'Our clients\'s needs are at the center of our business.' },
      { title: 'Quality', icon: 'faHome', description: 'We only represent properties that meet our high standards.' },
      { title: 'Integrity', icon: 'faHandshake', description: 'We conduct our business with honesty and transparency.' }
    ]
  });
  
  const [teamData, setTeamData] = useState({
    header: 'Meet Our Team',
    paragraph: 'Our experienced professionals are here to help you',
    members: []
  });
  
  const [testimonialsData, setTestimonialsData] = useState({
    header: 'What Our Clients Say',
    paragraph: 'Read testimonials from our satisfied clients',
    testimonials: []
  });
  
  // Fetch about page data
  useEffect(() => {
    const fetchAboutData = async () => {
      setLoading(true);
      setError(null);
      
      // Set up a timeout for the API call
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('API call timed out after 5 seconds'));
        }, 5000);
      });
      
      try {
        // Race between the API call and the timeout
        const response = await Promise.race([
          axios.get('/api/about'),
          timeoutPromise
        ]);
        
        if (response.data && response.data.status === 'success' && response.data.data) {
          const { hero, values, team, testimonials } = response.data.data;
          
          // Update hero section
          if (hero) {
            setAboutData({
              header: hero.header || 'About Our Company',
              paragraph: hero.paragraph || 'We\'re dedicated to helping you find your perfect property',
              image_path: hero.image_path,
              img: hero.image_path ? `/api/image/${hero.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/800x400?text=About+Our+Company'
            });
          }
          
          // Update values section
          if (values) {
            // Ensure values.values is an array
            let parsedValues;
            try {
              // If values.values is a string (JSON), parse it
              if (typeof values.values === 'string') {
                parsedValues = JSON.parse(values.values);
              } else if (Array.isArray(values.values)) {
                // If it's already an array, use it directly
                parsedValues = values.values;
              } else if (values.values && typeof values.values === 'object') {
                // If it's an object but not an array, convert to array if possible
                parsedValues = Object.values(values.values);
              } else {
                // Fallback to default values
                parsedValues = [
                  { title: 'Excellence', icon: 'faAward', description: 'We strive for excellence in everything we do.' },
                  { title: 'Client-Focused', icon: 'faUsers', description: 'Our clients\'s needs are at the center of our business.' },
                  { title: 'Quality', icon: 'faHome', description: 'We only represent properties that meet our high standards.' },
                  { title: 'Integrity', icon: 'faHandshake', description: 'We conduct our business with honesty and transparency.' }
                ];
              }
            } catch (e) {
              console.error('Error parsing values:', e);
              // Fallback to default values
              parsedValues = [
                { title: 'Excellence', icon: 'faAward', description: 'We strive for excellence in everything we do.' },
                { title: 'Client-Focused', icon: 'faUsers', description: 'Our clients\'s needs are at the center of our business.' },
                { title: 'Quality', icon: 'faHome', description: 'We only represent properties that meet our high standards.' },
                { title: 'Integrity', icon: 'faHandshake', description: 'We conduct our business with honesty and transparency.' }
              ];
            }
            
            setValuesData({
              header: values.header || 'Our Values',
              paragraph: values.paragraph || 'The principles that guide our business and service',
              values: parsedValues
            });
          }
          
          // Update team section
          if (team) {
            setTeamData({
              header: team.header || 'Meet Our Team',
              paragraph: team.paragraph || 'Our experienced professionals are here to help you!',
              members: team.members || []
            });
          }
          
          // Update testimonials section
          if (testimonials) {
            setTestimonialsData({
              header: testimonials.header || 'What Our Clients Say',
              paragraph: testimonials.paragraph || 'Read testimonials from our satisfied clients',
              testimonials: testimonials.testimonials || []
            });
          }
        }
      } catch (error) {
        console.error('Error fetching about page data:', error);
        setError('Failed to load about page data. Using default content.');
        // Use default data if API call fails - no need to set data as we have defaults
      } finally {
        // Always set loading to false to ensure the page renders
        setLoading(false);
      }
      
      // No need for additional timeout since we already have a timeout in the Promise.race
    };
    
    fetchAboutData();
  }, []);
  
  // Helper function to get the right icon component
  const getIcon = (iconName) => {
    return solidIcons[iconName] || solidIcons.faAward; // Default to faAward if icon not found
  };
  
  return (
    <div className="about-page">
      {/* Loading indicator */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading about page content...</p>
        </div>
      )}
      
      {/* Error message */}
      {error && !loading && (
        <div className="alert alert-warning m-4" role="alert">
          {error}
        </div>
      )}
      {/* Hero Section */}
      <section className="about-hero py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 style={{ color: '#a87758' }}>{aboutData.header}</h1>
              <p className="lead">{aboutData.paragraph}</p>
              <div className="stats-container d-flex mt-4">
                <div className="stat-item me-4">
                  <div className="stat-number">500+</div>
                  <div className="stat-label">Properties Sold</div>
                </div>
                <div className="stat-item me-4">
                  <div className="stat-number">1000+</div>
                  <div className="stat-label">Happy Clients</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">15+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              {aboutData.img ? (
                <img
                  src={aboutData.img}
                  alt="Our Team"
                  className="img-fluid rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.style.backgroundColor = '#f8f9fa';
                    e.target.parentElement.style.height = '300px';
                    e.target.parentElement.style.display = 'flex';
                    e.target.parentElement.style.alignItems = 'center';
                    e.target.parentElement.style.justifyContent = 'center';
                    e.target.parentElement.innerHTML = '<div>About Our Company</div>';
                  }}
                />
              ) : (
                <div 
                  className="rounded" 
                  style={{ 
                    backgroundColor: '#f8f9fa', 
                    height: '300px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#a87758',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  About Our Company
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="our-values py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 style={{ color: '#a87758' }}>{valuesData.header}</h2>
            <p>{valuesData.paragraph}</p>
          </div>
          <div className="row">
            {valuesData.values && Array.isArray(valuesData.values) && valuesData.values.map((value, index) => (
              <div className="col-md-3" key={index}>
                <div className="value-card text-center p-4">
                  <div className="icon-container mb-3">
                    <FontAwesomeIcon icon={getIcon(value.icon)} size="3x" style={{ color: '#a87758' }} />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="our-team py-5 bg-light">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 style={{ color: '#a87758' }}>{teamData.header}</h2>
            <p>{teamData.paragraph}</p>
          </div>
          <div className="row">
            {teamData.members && teamData.members.length > 0 ? (
              teamData.members.map((member, index) => (
                <div className="col-md-4" key={member.id || index}>
                  <div 
                    className="team-member-card bg-white p-4 text-center rounded shadow-sm mb-4" 
                    style={{ transition: 'all 0.3s ease' }} 
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-10px)';
                      e.currentTarget.style.boxShadow = '0 15px 30px rgba(0, 0, 0, 0.1)';
                    }} 
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    {member.image_path ? (
                      <img
                        src={`/api/image/${member.image_path.replace(/\\/g, '/')}`}
                        alt={member.name}
                        className="team-member-image rounded-circle mb-3"
                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = document.createElement('div');
                          placeholder.className = 'rounded-circle mb-3';
                          placeholder.style.width = '150px';
                          placeholder.style.height = '150px';
                          placeholder.style.backgroundColor = '#f8f9fa';
                          placeholder.style.display = 'flex';
                          placeholder.style.alignItems = 'center';
                          placeholder.style.justifyContent = 'center';
                          placeholder.style.color = '#a87758';
                          placeholder.style.fontSize = '1.5rem';
                          placeholder.style.margin = '0 auto';
                          placeholder.textContent = member.name.charAt(0);
                          e.target.parentNode.insertBefore(placeholder, e.target);
                        }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle mb-3" 
                        style={{ 
                          width: '150px', 
                          height: '150px', 
                          backgroundColor: '#f8f9fa', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: '#a87758',
                          fontSize: '1.5rem',
                          margin: '0 auto'
                        }}
                      >
                        {member.name.charAt(0)}
                      </div>
                    )}
                    <h3>{member.name}</h3>
                    <p className="text-muted">{member.position}</p>
                    <p>{member.paragraph}</p>
                    {member.social_links && (
                      <div className="social-icons" style={{ color: '#a87758' }}>
                        {member.social_links.linkedin && (
                          <a href={member.social_links.linkedin} className="me-2" target="_blank" rel="noopener noreferrer">
                            <i className="fab fa-linkedin"></i>
                          </a>
                        )}
                        {member.social_links.twitter && (
                          <a href={member.social_links.twitter} className="me-2" target="_blank" rel="noopener noreferrer">
                            <i className="fab fa-twitter"></i>
                          </a>
                        )}
                        {member.social_links.email && (
                          <a href={`mailto:${member.social_links.email}`} target="_blank" rel="noopener noreferrer">
                            <i className="fas fa-envelope"></i>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                <p>Our team information is coming soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 style={{ color: '#a87758' }}>{testimonialsData.header}</h2>
            <p>{testimonialsData.paragraph}</p>
          </div>
          <div className="row">
            {testimonialsData.testimonials && testimonialsData.testimonials.length > 0 ? (
              testimonialsData.testimonials.map((testimonial, index) => (
                <div className="col-md-4" key={testimonial.id || index}>
                  <div className="testimonial-card p-4 bg-light rounded mb-4 d-flex flex-column" style={{ minHeight: '300px' }}>
                    <div className="testimonial-text mb-3 flex-grow-1">
                      <div className="quote-mark" style={{ color: '#a87758', fontSize: '24px', marginBottom: '10px' }}>
                        &ldquo;
                      </div>
                      <p>{testimonial.paragraph}</p>
                    </div>
                    <div className="testimonial-author d-flex align-items-center" style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: 'auto' }}>
                      {testimonial.image_path ? (
                        <img
                          src={`/api/image/${testimonial.image_path.replace(/\\/g, '/')}`}
                          alt={testimonial.name}
                          className="rounded-circle me-3"
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = document.createElement('div');
                            placeholder.className = 'rounded-circle me-3';
                            placeholder.style.width = '50px';
                            placeholder.style.height = '50px';
                            placeholder.style.backgroundColor = '#f8f9fa';
                            placeholder.style.display = 'flex';
                            placeholder.style.alignItems = 'center';
                            placeholder.style.justifyContent = 'center';
                            placeholder.style.color = '#a87758';
                            placeholder.textContent = testimonial.name.charAt(0);
                            e.target.parentNode.insertBefore(placeholder, e.target);
                          }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle me-3" 
                          style={{ 
                            width: '50px', 
                            height: '50px', 
                            backgroundColor: '#f8f9fa', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: '#a87758'
                          }}
                        >
                          {testimonial.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <h5 className="mb-0">{testimonial.name}</h5>
                        <p className="text-muted mb-0">{testimonial.position}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                <p>Client testimonials coming soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
